import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Plus, FileText, BarChart3, Settings, Sparkles, ArrowRight, ChevronRight, TrendingUp, TrendingDown, Bell } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [anom, setAnom] = useState(null);
  const [err, setErr] = useState("");
  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => setErr("Could not load dashboard."));
    api.get("/trends/anomalies").then((r) => setAnom(r.data)).catch(() => {});
    api.get("/surveys").then((r) => setSurveys((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const totals = data?.totals || {};
  const trend = (data?.trend || []).map((t) => ({ ...t, label: t.date.slice(5) }));
  const first = (user?.name || "there").split(" ")[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-6 fade-in max-w-5xl mx-auto w-full">
      {/* Welcome hero */}
      <div className="brand-curve rounded-3xl p-6 md:p-8 relative overflow-hidden" style={{ boxShadow: "var(--shadow-soft)" }}>
        <div className="absolute -right-6 -bottom-8 opacity-90 pointer-events-none hidden sm:block">
          <img src="/brand/mascot-builder.jpg" alt="" className="w-40 h-40 object-contain mix-blend-multiply"/>
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm border" style={{ borderColor: "var(--border)" }}>
            <Bell size={14} style={{ color: "var(--brand)" }}/>
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--brand-strong)" }}>Welcome back</span>
          </div>
          <h1 className="mt-3 text-2xl md:text-3xl font-black tracking-tight" style={{ color: "var(--ink-900)" }}>
            Welcome back, {first}! <span className="inline-block">👋</span>
          </h1>
          <p className="mt-1 text-sm md:text-base" style={{ color: "var(--ink-500)" }}>
            {greet} — ready to create your next survey?
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/app/surveys/new" data-testid="dash-hero-cta">
              <button className="btn-pill text-sm">
                <Plus size={16} className="mr-1.5"/> New survey
              </button>
            </Link>
            <Link to="/app/templates">
              <button className="btn-pill ghost text-sm">Browse templates</button>
            </Link>
          </div>
        </div>
      </div>

      {/* 4 Quick action tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <ActionTile to="/app/surveys/new" testId="qa-create" tone="orange" title="Create" desc="New survey" icon={<Plus/>}/>
        <ActionTile to="/app/analytics" testId="qa-dashboard" tone="navy" title="Dashboard" desc="Insights" icon={<BarChart3/>}/>
        <ActionTile to="/app/surveys" testId="qa-manage" tone="orange-soft" title="Manage" desc="All surveys" icon={<FileText/>}/>
        <ActionTile to="/app/settings" testId="qa-settings" tone="grey" title="Settings" desc="Preferences" icon={<Settings/>}/>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Kpi data-testid="kpi-nps" label="NPS Score" value={totals.nps_score ?? "—"} tone="orange" />
        <Kpi data-testid="kpi-responses" label="Responses" value={totals.responses ?? 0} tone="navy" />
        <Kpi data-testid="kpi-promoters" label="Promoters" value={totals.promoters ?? 0} tone="emerald" />
        <Kpi data-testid="kpi-detractors" label="Detractors" value={totals.detractors ?? 0} tone="rose" />
      </div>

      {/* NPS trend + recent surveys */}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-5">
        <Card className="lg:col-span-2 p-5 md:p-6 border-0 rounded-3xl bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-black" style={{ color: "var(--ink-400)" }}>Last 14 days</p>
              <h3 className="text-lg font-extrabold" style={{ color: "var(--ink-900)" }}>NPS trend</h3>
            </div>
            <Link to="/app/analytics"><Button data-testid="dash-view-analytics" size="sm" variant="ghost" className="rounded-full font-bold" style={{ color: "var(--brand-strong)" }}>Details <ChevronRight size={14}/></Button></Link>
          </div>
          <div className="h-56 md:h-64 mt-4">
            {trend.length < 2 ? (
              <EmptyState title={trend.length === 0 ? "No responses yet" : "Not enough data yet"} desc={trend.length === 0 ? "Publish a survey to start seeing your NPS trend." : "Trend will appear once you have 2+ days of responses."}/>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="ag" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#F58220" stopOpacity="0.55"/>
                      <stop offset="1" stopColor="#F58220" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={30}/>
                  <Tooltip />
                  <Area type="monotone" dataKey="nps" stroke="#F58220" strokeWidth={2.6} fill="url(#ag)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5 md:p-6 border-0 rounded-3xl bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-black" style={{ color: "var(--ink-400)" }}>Recent</p>
              <h3 className="text-lg font-extrabold" style={{ color: "var(--ink-900)" }}>Surveys</h3>
            </div>
            <Link to="/app/surveys"><Button size="sm" variant="ghost" className="rounded-full font-bold" style={{ color: "var(--brand-strong)" }}>All <ChevronRight size={14}/></Button></Link>
          </div>
          <ul className="mt-4 space-y-2">
            {surveys.length === 0 && <li className="text-sm" style={{ color: "var(--ink-400)" }}>No surveys yet. Create one from the tile above.</li>}
            {surveys.map((s) => (
              <li key={s.id} data-testid={`recent-survey-${s.id}`}>
                <Link to={`/app/surveys/${s.id}`} className="flex items-center gap-3 p-3 rounded-2xl transition-colors hover:bg-slate-50">
                  <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: "var(--brand-soft)", color: "var(--brand-strong)" }}>
                    <FileText size={16}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold truncate" style={{ color: "var(--ink-900)" }}>{s.title}</p>
                    <p className="text-[11px] truncate" style={{ color: "var(--ink-400)" }}>{s.status || "draft"} · {s.questions?.length || 0} questions</p>
                  </div>
                  <ChevronRight size={16} style={{ color: "var(--ink-300)" }}/>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Anomaly / trend card */}
      {anom && (
        <Card className="p-5 md:p-6 border-0 rounded-3xl bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: anom.delta >= 0 ? "var(--promoter-bg)" : "var(--detractor-bg)", color: anom.delta >= 0 ? "var(--promoter)" : "var(--detractor)" }}>
              {anom.delta >= 0 ? <TrendingUp size={20}/> : <TrendingDown size={20}/>}
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider font-black" style={{ color: "var(--ink-400)" }}>7-day delta</p>
              <h3 className="text-lg font-extrabold" style={{ color: "var(--ink-900)" }}>
                NPS {anom.delta >= 0 ? "up" : "down"} {Math.abs(anom.delta)} points
              </h3>
              <p className="text-sm" style={{ color: "var(--ink-500)" }}>
                Last 7 days: <strong className="tabular-nums">{anom.last7_avg}</strong> · Prior 7d: <strong className="tabular-nums">{anom.prev7_avg}</strong> · {anom.anomalies?.length || 0} anomalies detected
              </p>
            </div>
            <Link to="/app/ai" className="hidden md:inline">
              <button className="btn-pill text-xs">
                <Sparkles size={14} className="mr-1.5"/> Ask AI why
              </button>
            </Link>
          </div>
        </Card>
      )}

      {err && <p className="text-rose-600 text-sm">{err}</p>}
    </div>
  );
}

function ActionTile({ to, icon, title, desc, tone, testId }) {
  const toneMap = {
    "orange":       { bg: "linear-gradient(180deg, #FFFFFF 0%, #FFF6EA 100%)", iconBg: "linear-gradient(135deg, #FFB273 0%, #F58220 100%)", iconColor: "#FFFFFF" },
    "navy":         { bg: "linear-gradient(180deg, #FFFFFF 0%, #EEF2FA 100%)", iconBg: "linear-gradient(135deg, #4F6FA5 0%, #1F3A6B 100%)", iconColor: "#FFFFFF" },
    "orange-soft":  { bg: "linear-gradient(180deg, #FFFFFF 0%, #FFF0DF 100%)", iconBg: "#FFE1BE", iconColor: "#E36F0E" },
    "grey":         { bg: "linear-gradient(180deg, #FFFFFF 0%, #F5F3EE 100%)", iconBg: "#ECE9E2", iconColor: "#3D3B36" },
  };
  const t = toneMap[tone] || toneMap.orange;
  return (
    <Link to={to} data-testid={testId}>
      <div className="tile flex flex-col items-start gap-3 h-full" style={{ background: t.bg }}>
        <div className="w-12 h-12 rounded-2xl grid place-items-center shadow" style={{ background: t.iconBg, color: t.iconColor }}>
          {icon}
        </div>
        <div>
          <p className="text-[15px] font-black" style={{ color: "var(--ink-900)" }}>{title}</p>
          <p className="text-[11px] font-semibold" style={{ color: "var(--ink-500)" }}>{desc}</p>
        </div>
      </div>
    </Link>
  );
}

function Kpi({ label, value, tone, ...rest }) {
  const map = {
    orange:  { color: "var(--brand-strong)", chip: "var(--brand-soft)" },
    navy:    { color: "var(--secondary)",   chip: "var(--secondary-soft)" },
    emerald: { color: "#059669",            chip: "#ECFDF5" },
    rose:    { color: "#DC2626",            chip: "#FEF2F2" },
  };
  const t = map[tone] || map.orange;
  return (
    <Card {...rest} className="p-4 md:p-5 border-0 rounded-3xl bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: t.chip, color: t.color }}>{label}</span>
      <p className="mt-2 text-3xl md:text-4xl font-black tabular-nums" style={{ color: t.color }}>{value}</p>
    </Card>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="h-full grid place-items-center text-center">
      <div>
        <p className="font-black" style={{ color: "var(--ink-900)" }}>{title}</p>
        <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>{desc}</p>
      </div>
    </div>
  );
}
